import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BookLocation } from '../../../../dorian-shared/types/bookLocation/bookLocation';
import { Branch } from '../../../../dorian-shared/types/branch/Branch';
import { MemoryDTO } from '../../Book/MemoryBank/memoryBankTypes';
import { EpisodeTextImport } from '../../../../services/EpisodeTextService/models/EpisodeTextImport';
import { EpisodeTextImportNode } from '../../../../services/EpisodeTextService/models/EpisodeTextImportNode';
import { EpisodeTextImportStep } from '../../../../services/EpisodeTextService/models/EpisodeTextImportStep';
import { EpisodeTextImportHelper } from '../../../../services/EpisodeTextService/models/EpisodeTextImportHelper';
import { importNodeToTsv } from '../../../../services/EpisodeTextService/utils/importNodeToTsv';
import { sortNodesByPlayerFlow } from '../utils/sortNodesByPlayerFlow';
import {
  CsvEditNodeGroup,
  CsvEditRow,
  EditableField,
  NODE_FIELD_MAP,
  STEP_FIELD_MAP,
  isEditabilityDowngrade,
} from '../types/selectiveEditTypes';

// --- Safe cast helper (F2) ---

function safeCastRows(result: unknown): CsvEditRow[] {
  if (!Array.isArray(result)) {
    console.warn('[useSelectiveEditData] validateAndCreateTable did not return an array');
    return [];
  }
  return result as CsvEditRow[];
}

// --- findStepById utility ---

type FindResult =
  | { kind: 'node'; target: EpisodeTextImportNode }
  | { kind: 'step'; target: EpisodeTextImportStep }
  | null;

/**
 * Finds a node or step by id across episodeTextImport.nodes, including nested conditionListItems.
 * Only one level of conditionListItems is traversed (parser never produces deeper nesting).
 */
function findStepById(episodeTextImport: EpisodeTextImport, id: string): FindResult {
  for (const node of episodeTextImport.nodes) {
    if (node.id === id) {
      return { kind: 'node', target: node };
    }
    for (const step of node.steps) {
      if (step.id === id) {
        return { kind: 'step', target: step };
      }
      for (const condition of step.conditionListItems) {
        if (condition.id === id) {
          return { kind: 'step', target: condition };
        }
      }
    }
  }
  return null;
}

// --- findParentNodeIndex utility ---

/**
 * Finds the index of the parent node that contains the given row ID
 * (as node.id, step.id, or conditionListItem.id).
 */
function findParentNodeIndex(episodeTextImport: EpisodeTextImport, rowId: string): number {
  for (let i = 0; i < episodeTextImport.nodes.length; i++) {
    const node = episodeTextImport.nodes[i];
    if (node.id === rowId) return i;
    for (const step of node.steps) {
      if (step.id === rowId) return i;
      for (const condition of step.conditionListItems) {
        if (condition.id === rowId) return i;
      }
    }
  }
  return -1;
}

type MutateResult = { nodeIndex: number; error?: string };

/**
 * Mutates a field on a found node/step in-place, then reparses the parent node
 * via TSV round-trip to fix step types (e.g. Narration→Dialogue when speaker added).
 *
 * Guard: if the reparse would turn an editable step into a fully read-only type
 * (e.g. Remember→Video), the mutation is rolled back and an error is returned.
 */
function mutateFieldAndReparseNode(
  episodeTextImport: EpisodeTextImport,
  rowId: string,
  field: EditableField,
  value: string,
): MutateResult {
  const found = findStepById(episodeTextImport, rowId);
  if (!found) return { nodeIndex: -1 };

  const propName = found.kind === 'node' ? NODE_FIELD_MAP[field] : STEP_FIELD_MAP[field];
  if (!propName) return { nodeIndex: -1 };

  // Capture pre-edit state for potential rollback
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const oldValue: string = (found.target as any)[propName];
  const oldType = found.kind === 'step' ? found.target.type : undefined;

  // Mutate the field in-place
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (found.target as any)[propName] = value;

  // Find parent node and reparse it
  const nodeIndex = findParentNodeIndex(episodeTextImport, rowId);
  if (nodeIndex === -1) return { nodeIndex: -1 };
  reparseNodeAtIndex(episodeTextImport, nodeIndex);

  // Guard: block editability downgrade on the edited field (DOR-10363)
  if (oldType) {
    const newNode = episodeTextImport.nodes[nodeIndex];
    const newStep = newNode?.steps.find((s) => s.id === rowId)
      ?? newNode?.steps.flatMap((s) => s.conditionListItems).find((c) => c.id === rowId);

    if (newStep?.type && isEditabilityDowngrade(oldType, newStep.type, field)) {
      // Rollback: restore old value and reparse back to original type
      const restoredFound = findStepById(episodeTextImport, rowId);
      if (restoredFound) {
        const rProp = restoredFound.kind === 'node' ? NODE_FIELD_MAP[field] : STEP_FIELD_MAP[field];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (rProp) (restoredFound.target as any)[rProp] = oldValue;
      }
      reparseNodeAtIndex(episodeTextImport, nodeIndex);
      return {
        nodeIndex,
        error: `Cannot change to ${newStep.type} — row would become non-editable`,
      };
    }
  }

  return { nodeIndex };
}

/**
 * Reparses a node at a given index via TSV round-trip.
 * Replaces `episodeTextImport.nodes[nodeIndex]` with a fresh node
 * that has correct step types from `getRowType()` and preserved real IDs.
 */
function reparseNodeAtIndex(episodeTextImport: EpisodeTextImport, nodeIndex: number): void {
  const node = episodeTextImport.nodes[nodeIndex];
  const tsv = importNodeToTsv(node);
  const reparsed = EpisodeTextImportHelper.parseTextToEpisodeText(tsv);
  if (reparsed && reparsed.nodes.length > 0) {
    episodeTextImport.nodes[nodeIndex] = reparsed.nodes[0];
  } else {
    // Reparse failed (e.g. empty node title) — at least sync commands
    node.reparseCommands();
    for (const step of node.steps) {
      step.reparseCommands();
      for (const cond of step.conditionListItems) {
        cond.reparseCommands();
      }
    }
  }
}

// --- Grouping utility ---

/**
 * Builds a single CsvEditNodeGroup from a node and a pre-built id→row map.
 * Used both by buildNodeGroups (full rebuild) and updateRowField (per-node update — T7).
 */
function buildNodeGroupFromRowMap(node: EpisodeTextImportNode, rowById: Map<string, CsvEditRow>): CsvEditNodeGroup | null {
  const nodeRow = rowById.get(node.id);
  if (!nodeRow) {
    console.warn(`[useSelectiveEditData] No flat row found for node id=${node.id}, title=${node.title}`);
    return null;
  }

  const stepRows: CsvEditRow[] = [];
  for (const step of node.steps) {
    const stepRow = rowById.get(step.id);
    if (stepRow) {
      stepRows.push(stepRow);
    }
    // Include nested conditionListItems
    for (const condition of step.conditionListItems) {
      const condRow = rowById.get(condition.id);
      if (condRow) {
        stepRows.push(condRow);
      }
    }
  }

  const allRows = [nodeRow, ...stepRows];
  const searchableText = allRows
    .map((r) => `${r.nodeOrSpeaker}\t${r.text}\t${r.description}`)
    .join('\t');

  const nodeNameText = nodeRow.nodeOrSpeaker;
  const stepContentText = stepRows
    .map((r) => `${r.nodeOrSpeaker}\t${r.text}\t${r.description}`)
    .join('\t');
  const speakerText = stepRows
    .map((r) => r.nodeOrSpeaker)
    .join('\t');

  return { nodeRow, stepRows, searchableText, nodeNameText, stepContentText, speakerText };
}

const sortedNodesCache = new WeakMap<EpisodeTextImportNode[], EpisodeTextImportNode[]>();

function buildNodeGroups(
  episodeTextImport: EpisodeTextImport,
  flatRows: CsvEditRow[],
  branches?: Branch[],
): CsvEditNodeGroup[] {
  const rowById = new Map<string, CsvEditRow>();
  for (const row of flatRows) {
    rowById.set(row.id, row);
  }

  let sortedNodes: EpisodeTextImportNode[];
  if (branches && branches.length > 0) {
    const cached = sortedNodesCache.get(episodeTextImport.nodes);
    if (cached) {
      sortedNodes = cached;
    } else {
      sortedNodes = sortNodesByPlayerFlow(episodeTextImport.nodes, branches);
      sortedNodesCache.set(episodeTextImport.nodes, sortedNodes);
    }
  } else {
    sortedNodes = episodeTextImport.nodes;
  }

  return sortedNodes.map((node) => buildNodeGroupFromRowMap(node, rowById)).filter(Boolean) as CsvEditNodeGroup[];
}

// --- Snapshot type ---

type SnapshotEntry = { nodeOrSpeaker: string; text: string; description: string };

function buildSnapshot(flatRows: CsvEditRow[]): Map<string, SnapshotEntry> {
  const snapshot = new Map<string, SnapshotEntry>();
  for (const row of flatRows) {
    snapshot.set(row.id, {
      nodeOrSpeaker: row.nodeOrSpeaker,
      text: row.text,
      description: row.description,
    });
  }
  return snapshot;
}

// --- Navigation map ---

function buildNavigationMap(
  episodeTextImport: EpisodeTextImport,
  nodesInEpisode: Branch[],
): Map<string, number> {
  const navMap = new Map<string, number>();
  for (const importNode of episodeTextImport.nodes) {
    const titleKey = importNode.title.toLowerCase();
    if (navMap.has(titleKey)) {
      continue; // first match wins, duplicates already flagged by validation
    }
    // Prefer realBranchId (stable through title edits) over title-based matching
    if (importNode.realBranchId != null) {
      navMap.set(titleKey, importNode.realBranchId);
    } else {
      const branchMatch = nodesInEpisode.find(
        (b) => b.title.toLowerCase() === titleKey,
      );
      if (branchMatch) {
        navMap.set(titleKey, branchMatch.id);
      } else {
        console.warn(`[useSelectiveEditData] No Branch match for node title="${importNode.title}"`);
      }
    }
  }
  return navMap;
}

// --- Hook ---

export type CsvSelectiveEditDataState = {
  nodeGroups: CsvEditNodeGroup[];
  updateRowField: (rowId: string, field: EditableField, value: string) => void;
  revertNode: (nodeId: string) => void;
  isNodeDirty: (nodeId: string) => boolean;
  isCellDirty: (rowId: string, field: string) => boolean;
  dirtyNodeIds: string[];
  navigationMap: Map<string, number>;
  runValidation: () => void;
};

export function useSelectiveEditData(
  episodeTextImport: EpisodeTextImport | null,
  nodesInEpisode: Branch[],
  characterNamesInBook: string[],
  bookLocations: BookLocation[],
  memoriesInBook: MemoryDTO[],
  rewardIds?: Map<string, number>,
): CsvSelectiveEditDataState {
  const [nodeGroups, setNodeGroups] = useState<CsvEditNodeGroup[]>([]);
  const snapshotRef = useRef<Map<string, SnapshotEntry>>(new Map());
  const prevImportRef = useRef<EpisodeTextImport | null>(null);

  // Stable refs to avoid stale closures in callbacks (R3 + V4)
  const importRef = useRef(episodeTextImport);
  importRef.current = episodeTextImport;
  const nodesRef = useRef(nodesInEpisode);
  const charsRef = useRef(characterNamesInBook);
  const locsRef = useRef(bookLocations);
  const memsRef = useRef(memoriesInBook);
  const achievementsRef = useRef(rewardIds);
  nodesRef.current = nodesInEpisode;
  charsRef.current = characterNamesInBook;
  locsRef.current = bookLocations;
  memsRef.current = memoriesInBook;
  achievementsRef.current = rewardIds;

  // Track whether initial import has been processed (F6)
  const initializedRef = useRef(false);

  // Initialize / reset when episodeTextImport identity changes (F3 + F7)
  useEffect(() => {
    if (episodeTextImport === prevImportRef.current) return;
    prevImportRef.current = episodeTextImport;
    if (!episodeTextImport) {
      setNodeGroups([]);
      snapshotRef.current = new Map();
      return;
    }
    try {
      // Instant render — build table without validation (errors: []).
      // Validation runs per-node on edit, or full via Validate button.
      const fastRows = safeCastRows(episodeTextImport.createTableWithoutValidation());
      snapshotRef.current = buildSnapshot(fastRows);
      const groups = buildNodeGroups(episodeTextImport, fastRows, nodesInEpisode);
      setNodeGroups(groups);
      initializedRef.current = true;
    } catch (error) {
      console.error('[useSelectiveEditData] Initial createTable failed:', error);
    }
  }, [episodeTextImport]);

  // All callbacks read importRef.current for latest value (V4)
  const revalidateAndRegroup = useCallback(() => {
    const current = importRef.current;
    if (!current) return;
    try {
      const rawResult = current.validateAndCreateTable(
        nodesRef.current,
        charsRef.current,
        locsRef.current,
        memsRef.current,
        true, // skipNodeConflictCheck: selective-edit edits existing nodes
        achievementsRef.current,
      );
      const flatRows = safeCastRows(rawResult);
      const groups = buildNodeGroups(current, flatRows, nodesRef.current);
      setNodeGroups(groups);
    } catch (error) {
      console.error('[useSelectiveEditData] validateAndCreateTable failed, keeping previous state:', error);
    }
  }, []);

  const updateRowField = useCallback((rowId: string, field: EditableField, value: string) => {
    const current = importRef.current;
    if (!current) return;
    const result = mutateFieldAndReparseNode(current, rowId, field, value);
    if (result.nodeIndex === -1) {
      console.warn(`[useSelectiveEditData] updateRowField: row id=${rowId} not found or reparse failed`);
      return;
    }

    // T7: Per-node revalidation — only revalidate and re-render the affected node.
    // In Selective Edit, users cannot edit node names or add/remove nodes, so cross-node
    // checks (duplicate names, "only ONE ENDING") cannot be triggered by a field edit.
    // realBranchId/realStepId are fixed server values — duplicate ID check is unnecessary here.
    const newNode = current.nodes[result.nodeIndex];
    if (!newNode) return;

    const config = current.buildValidateNodeConfig(
      nodesRef.current,
      charsRef.current,
      locsRef.current,
      memsRef.current,
      true, // skipNodeConflictCheck — Selective Edit mode
      achievementsRef.current,
    );
    const nodeRows = current.validateAndCreateTableForNode(newNode, config) as CsvEditRow[];

    // Inject readonly-trap guard error (DOR-10363)
    if (result.error) {
      const affectedRow = nodeRows.find((r) => r.id === rowId);
      if (affectedRow) affectedRow.errors.push(result.error);
    }

    const rowById = new Map<string, CsvEditRow>(nodeRows.map((r) => [r.id, r]));
    const newGroup = buildNodeGroupFromRowMap(newNode, rowById);
    if (!newGroup) return;

    setNodeGroups((prev) => prev.map((g) => (g.nodeRow.id === newNode.id ? newGroup : g)));
  }, []);

  const revertNode = useCallback((nodeId: string) => {
    const current = importRef.current;
    if (!current) return;
    const nodeIndex = current.nodes.findIndex((n) => n.id === nodeId);
    if (nodeIndex === -1) return;
    const node = current.nodes[nodeIndex];

    // Restore original field values from snapshot
    const restoreTarget = (target: EpisodeTextImportNode | EpisodeTextImportStep, id: string, isNode: boolean) => {
      const original = snapshotRef.current.get(id);
      if (!original) return;
      if (isNode) {
        (target as EpisodeTextImportNode).title = original.nodeOrSpeaker;
        (target as EpisodeTextImportNode).text = original.text;
        (target as EpisodeTextImportNode).description = original.description;
      } else {
        (target as EpisodeTextImportStep).speakerName = original.nodeOrSpeaker;
        (target as EpisodeTextImportStep).speakerText = original.text;
        (target as EpisodeTextImportStep).description = original.description;
      }
    };

    restoreTarget(node, node.id, true);
    for (const step of node.steps) {
      restoreTarget(step, step.id, false);
      for (const cond of step.conditionListItems) {
        restoreTarget(cond, cond.id, false);
      }
    }

    // TSV reparse to fix step.type (e.g. Dialogue→Narration when speaker was cleared)
    // and to regenerate consistent model objects after field restoration.
    reparseNodeAtIndex(current, nodeIndex);

    // Per-node revalidation (same as updateRowField) — no need for full revalidation
    const revertedNode = current.nodes[nodeIndex];
    if (!revertedNode) return;
    const config = current.buildValidateNodeConfig(
      nodesRef.current,
      charsRef.current,
      locsRef.current,
      memsRef.current,
      true,
    );
    const nodeRows = current.validateAndCreateTableForNode(revertedNode, config) as CsvEditRow[];
    const rowById = new Map<string, CsvEditRow>(nodeRows.map((r) => [r.id, r]));
    const newGroup = buildNodeGroupFromRowMap(revertedNode, rowById);
    if (!newGroup) return;
    setNodeGroups((prev) => prev.map((g) => (g.nodeRow.id === revertedNode.id ? newGroup : g)));
  }, []);

  // O(1) row lookup for dirty checks (V11)
  const rowLookup = useMemo(() => {
    const map = new Map<string, { row: CsvEditRow; nodeId: string }>();
    for (const group of nodeGroups) {
      map.set(group.nodeRow.id, { row: group.nodeRow, nodeId: group.nodeRow.id });
      for (const sr of group.stepRows) {
        map.set(sr.id, { row: sr, nodeId: group.nodeRow.id });
      }
    }
    return map;
  }, [nodeGroups]);

  const isCellDirty = useCallback((rowId: string, field: string) => {
    const original = snapshotRef.current.get(rowId);
    if (!original) return false;
    const entry = rowLookup.get(rowId);
    if (!entry) return false;
    const fieldKey = field as keyof SnapshotEntry;
    if (fieldKey === 'nodeOrSpeaker' || fieldKey === 'text' || fieldKey === 'description') {
      return entry.row[fieldKey] !== original[fieldKey];
    }
    return false;
  }, [rowLookup]);

  // Compute dirty node IDs once via useMemo — O(1) lookup per call (F9)
  const dirtyNodeIdSet = useMemo(() => {
    const set = new Set<string>();
    for (const g of nodeGroups) {
      const allRows = [g.nodeRow, ...g.stepRows];
      const isDirty = allRows.some((row) => {
        const original = snapshotRef.current.get(row.id);
        if (!original) return false;
        return (
          row.nodeOrSpeaker !== original.nodeOrSpeaker
          || row.text !== original.text
          || row.description !== original.description
        );
      });
      if (isDirty) set.add(g.nodeRow.id);
    }
    return set;
  }, [nodeGroups]);

  const isNodeDirty = useCallback((nodeId: string) => dirtyNodeIdSet.has(nodeId), [dirtyNodeIdSet]);

  const dirtyNodeIds = useMemo(() => Array.from(dirtyNodeIdSet), [dirtyNodeIdSet]);

  const navigationMap = useMemo(
    () => (episodeTextImport ? buildNavigationMap(episodeTextImport, nodesInEpisode) : new Map<string, number>()),
    [episodeTextImport, nodesInEpisode],
  );

  return {
    nodeGroups,
    updateRowField,
    revertNode,
    isNodeDirty,
    isCellDirty,
    dirtyNodeIds,
    navigationMap,
    runValidation: revalidateAndRegroup,
  };
}
